import React, { useState, useEffect } from 'react';
import { 
  FiUser, 
  FiLock, 
  FiSave, 
  FiEye, 
  FiEyeOff,
  FiShield,
  FiToggleLeft,
  FiToggleRight,
  FiKey,
  FiCopy,
  FiRefreshCw,
  FiX,
  FiCheck,
  FiBook
} from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import LoginAuthUser from '../../baseUrl/LoginAuthUser';
import ApiDocs from '../ApiDocs/ApiDocs';
import './Settings.css';

interface UserSettings {
  // Advanced Settings
  autoLogout: boolean;
  autoLogoutTime: number;
  twoFactorAuth: boolean;
  sessionTimeout: number;
  rememberDevice: boolean;
}

const Settings: React.FC = () => {
  const { user, updateUserProfile, changePassword, refreshUser } = useAuth();
  
  // Active tab state
  const [activeTab, setActiveTab] = useState('general');
  
  // Profile form state
  const [profileData, setProfileData] = useState({
    email: ''
  });
  
  // Password form state
  const [passwordData, setPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  // User settings state
  const [settings, setSettings] = useState<UserSettings>({
    // Advanced Settings
    autoLogout: true,
    autoLogoutTime: 30,
    twoFactorAuth: false,
    sessionTimeout: 60,
    rememberDevice: true
  });
  
  // Show/hide password states
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Loading states
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  
  // Settings changed tracker
  const [settingsChanged, setSettingsChanged] = useState(false);

  // API Key states
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [displayedApiKey, setDisplayedApiKey] = useState<string | null>(null);
  const [apiKeyLoading, setApiKeyLoading] = useState(false);
  const [apiKeyCopied, setApiKeyCopied] = useState(false);
  const [apiKeyCopying, setApiKeyCopying] = useState(false);
  const [isNewlyGenerated, setIsNewlyGenerated] = useState(false);
  const [apiKeyCreatedAt, setApiKeyCreatedAt] = useState<string | null>(null);

  // Update profile data when user changes
  useEffect(() => {
    if (user) {
      setProfileData({
        email: user.email || ''
      });
    }
  }, [user]);

  // Load user settings on mount
  useEffect(() => {
    loadUserSettings();
  }, []);

  // Track settings changes
  useEffect(() => {
    setSettingsChanged(true);
  }, [settings]);

  const loadUserSettings = async () => {
    try {
      const response = await LoginAuthUser.getUserSettings();
      if (response.success && response.data) {
        setSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const saveSettings = async () => {
    setIsSavingSettings(true);
    try {
      const response = await LoginAuthUser.updateUserSettings(settings);
      if (response.success) {
        toast.success('Settings saved successfully');
        setSettingsChanged(false);
      } else {
        toast.error('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setIsSavingSettings(false);
    }
  };

  // Toggle functions for boolean settings
  const toggleSetting = (key: keyof UserSettings) => {
    setSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Handle profile update
  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsUpdatingProfile(true);
    
    try {
      await updateUserProfile(profileData);
      // Refresh user data after successful update
      await refreshUser();
    } catch (error) {
      console.error('Profile update error:', error);
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  // Handle password change
  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (passwordData.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters long');
      return;
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordData.oldPassword === passwordData.newPassword) {
      toast.error('New password must be different from current password');
      return;
    }
    
    setIsChangingPassword(true);
    
    try {
      const result = await changePassword(passwordData.oldPassword, passwordData.newPassword);
      
      if (result.success) {
        toast.success('Password changed successfully');
        setPasswordData({
          oldPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        toast.error(result.message || 'Failed to change password');
      }
    } catch (error) {
      console.error('Password change error:', error);
      toast.error('An error occurred while changing password');
    } finally {
      setIsChangingPassword(false);
    }
  };

  // Copy API key to clipboard (calls API to get full key)
  const handleCopyApiKey = async () => {
    if (user?.role !== 'admin') {
      toast.error('Only admin users can copy API keys');
      return;
    }

    setApiKeyCopying(true);
    
    try {
      // Call API to get full API key
      const response = await LoginAuthUser.copyMyApiKey();
      
      if (response.success && response.data?.apiKey) {
        const fullApiKey = response.data.apiKey;
        
        // Copy to clipboard
        try {
          await navigator.clipboard.writeText(fullApiKey);
          setApiKeyCopied(true);
          toast.success('API key copied to clipboard!');
          
          // Reset copied state after 3 seconds
          setTimeout(() => {
            setApiKeyCopied(false);
          }, 3000);
        } catch (clipboardError) {
          // Fallback for older browsers
          try {
            const textArea = document.createElement('textarea');
            textArea.value = fullApiKey;
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            textArea.style.top = '-999999px';
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            document.execCommand('copy');
            textArea.remove();
            setApiKeyCopied(true);
            toast.success('API key copied to clipboard!');
            
            setTimeout(() => {
              setApiKeyCopied(false);
            }, 3000);
          } catch (fallbackError) {
            toast.error('Failed to copy API key. Please try again.');
          }
        }
      } else {
        toast.error(response.message || 'Failed to get API key');
      }
    } catch (error: any) {
      console.error('Error copying API key:', error);
      toast.error('Failed to copy API key');
    } finally {
      setApiKeyCopying(false);
    }
  };

  // Generate new API key
  const handleGenerateApiKey = async () => {
    if (user?.role !== 'admin') {
      toast.error('Only admin users can generate API keys');
      return;
    }

    const confirmMessage = user?.hasApiKey
      ? 'This will regenerate your API key. The old key will be invalidated. Continue?'
      : 'Generate a new API key for programmatic access?';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setApiKeyLoading(true);
    setApiKeyCopied(false);
    setIsNewlyGenerated(true);
    
    try {
      const response = await LoginAuthUser.generateMyApiKey();
      
      if (response.success && response.data) {
        setDisplayedApiKey(response.data.apiKey);
        setApiKeyCreatedAt(response.data.createdAt);
        setShowApiKeyModal(true);
        await refreshUser(); // Refresh user data to update apiKey status
        toast.success('API key generated successfully');
      } else {
        toast.error(response.message || 'Failed to generate API key');
      }
    } catch (error: any) {
      console.error('Error generating API key:', error);
      toast.error('Failed to generate API key');
    } finally {
      setApiKeyLoading(false);
    }
  };

  const handleRevokeApiKey = async () => {
    if (!user?.hasApiKey) {
      toast.error('You do not have an API key');
      return;
    }

    if (!window.confirm('Are you sure you want to revoke your API key? This action cannot be undone.')) {
      return;
    }

    setApiKeyLoading(true);
    try {
      const response = await LoginAuthUser.revokeMyApiKey();
      
      if (response.success) {
        await refreshUser(); // Refresh user data
        toast.success('API key revoked successfully');
      } else {
        toast.error(response.message || 'Failed to revoke API key');
      }
    } catch (error: any) {
      console.error('Error revoking API key:', error);
      toast.error('Failed to revoke API key');
    } finally {
      setApiKeyLoading(false);
    }
  };

  const copyModalApiKeyToClipboard = async () => {
    if (displayedApiKey) {
      try {
        await navigator.clipboard.writeText(displayedApiKey);
        setApiKeyCopied(true);
        toast.success('API key copied to clipboard!');
        
        // Reset copied state after 3 seconds
        setTimeout(() => {
          setApiKeyCopied(false);
        }, 3000);
      } catch (error) {
        // Fallback for older browsers
        try {
          const textArea = document.createElement('textarea');
          textArea.value = displayedApiKey;
          textArea.style.position = 'fixed';
          textArea.style.left = '-999999px';
          textArea.style.top = '-999999px';
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          document.execCommand('copy');
          textArea.remove();
          setApiKeyCopied(true);
          toast.success('API key copied to clipboard!');
          
          setTimeout(() => {
            setApiKeyCopied(false);
          }, 3000);
        } catch (fallbackError) {
          toast.error('Failed to copy API key. Please copy it manually.');
        }
      }
    }
  };

  const closeApiKeyModal = () => {
    setShowApiKeyModal(false);
    setDisplayedApiKey(null);
    setApiKeyCopied(false);
    setIsNewlyGenerated(false);
    setApiKeyCreatedAt(null);
  };

  const ToggleSwitch = ({ enabled, onToggle }: { enabled: boolean; onToggle: () => void }) => (
    <button
      type="button"
      className={`toggle-switch ${enabled ? 'enabled' : ''}`}
      onClick={onToggle}
    >
      {enabled ? <FiToggleRight /> : <FiToggleLeft />}
    </button>
  );

  return (
    <div className="settings-container">
      <div className="settings-header">
        <h1>Settings</h1>
        <p>Manage your account settings and preferences</p>
      </div>

      <div className="settings-content">
        <div className="settings-tabs">
          <button
            className={`tab-button ${activeTab === 'general' ? 'active' : ''}`}
            onClick={() => setActiveTab('general')}
          >
            <FiUser />
            <span>General</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'security' ? 'active' : ''}`}
            onClick={() => setActiveTab('security')}
          >
            <FiLock />
            <span>Security</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'api-docs' ? 'active' : ''}`}
            onClick={() => setActiveTab('api-docs')}
          >
            <FiBook />
            <span>API Docs</span>
          </button>
        </div>

        <div className="settings-body">
          {/* General Tab */}
          {activeTab === 'general' && (
            <div className="settings-section">
              <h2>Profile Information</h2>
              <form onSubmit={handleProfileUpdate} className="settings-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="username">Username</label>
                    <input
                      type="text"
                      id="username"
                      value={user?.username || ''}
                      disabled
                      className="form-control disabled"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="role">Role</label>
                    <input
                      type="text"
                      id="role"
                      value={user?.role === 'admin' ? 'Administrator' : 'User'}
                      disabled
                      className="form-control disabled"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    value={profileData.email}
                    onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                    className="form-control"
                    placeholder="Enter your email address"
                  />
                </div>

                <div className="form-actions">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isUpdatingProfile}
                  >
                    <FiSave />
                    {isUpdatingProfile ? 'Updating...' : 'Update Profile'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div className="settings-section">
              <h2>Security Settings</h2>
              
              <div className="settings-group">
                <h3><FiLock /> Password</h3>
                <form onSubmit={handlePasswordChange} className="settings-form">
                  <div className="form-group">
                    <label htmlFor="oldPassword">Current Password</label>
                    <div className="password-input-wrapper">
                      <input
                        type={showOldPassword ? 'text' : 'password'}
                        id="oldPassword"
                        value={passwordData.oldPassword}
                        onChange={(e) => setPasswordData({ ...passwordData, oldPassword: e.target.value })}
                        className="form-control"
                        placeholder="Enter your current password"
                        required
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowOldPassword(!showOldPassword)}
                      >
                        {showOldPassword ? <FiEyeOff /> : <FiEye />}
                      </button>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="newPassword">New Password</label>
                    <div className="password-input-wrapper">
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        id="newPassword"
                        value={passwordData.newPassword}
                        onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                        className="form-control"
                        placeholder="Enter your new password"
                        required
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                      >
                        {showNewPassword ? <FiEyeOff /> : <FiEye />}
                      </button>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="confirmPassword">Confirm New Password</label>
                    <div className="password-input-wrapper">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        id="confirmPassword"
                        value={passwordData.confirmPassword}
                        onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                        className="form-control"
                        placeholder="Confirm your new password"
                        required
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                      </button>
                    </div>
                  </div>

                  <div className="form-actions">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={isChangingPassword}
                    >
                      <FiLock />
                      {isChangingPassword ? 'Changing...' : 'Change Password'}
                    </button>
                  </div>
                </form>
              </div>

              {/* API Key Section - Only for Admin Users */}
              {user?.role === 'admin' && (
                <div className="settings-group">
                  <h3><FiKey /> API Key</h3>
                  <p className="settings-description">
                    Generate an API key for programmatic access to the system. API keys are only available for admin users.
                  </p>
                  
                  <div className="api-key-section">
                    {user?.hasApiKey ? (
                      <div className="api-key-status">
                        <div className="api-key-info">
                          <div className="api-key-display-inline">
                            <span className="api-key-badge has-key">
                              <FiKey /> {user.apiKey}
                            </span>
                            <button
                              className={`btn-copy-inline ${apiKeyCopied ? 'copied' : ''}`}
                              onClick={handleCopyApiKey}
                              disabled={apiKeyCopying}
                              title={apiKeyCopied ? 'Copied!' : 'Copy full API key'}
                            >
                              {apiKeyCopied ? <FiCheck /> : <FiCopy />}
                            </button>
                          </div>
                          {user?.apiKeyCreatedAt && (
                            <span className="api-key-created">
                              Created: {new Date(user.apiKeyCreatedAt).toLocaleString()}
                            </span>
                          )}
                        </div>
                        <div className="api-key-actions">
                          <button
                            className="btn btn-secondary"
                            onClick={handleGenerateApiKey}
                            disabled={apiKeyLoading}
                          >
                            <FiRefreshCw />
                            {apiKeyLoading ? 'Regenerating...' : 'Regenerate'}
                          </button>
                          <button
                            className="btn btn-danger"
                            onClick={handleRevokeApiKey}
                            disabled={apiKeyLoading}
                          >
                            <FiX />
                            Revoke
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="api-key-status">
                        <span className="api-key-badge no-key">No API key generated</span>
                        <button
                          className="btn btn-primary"
                          onClick={handleGenerateApiKey}
                          disabled={apiKeyLoading}
                        >
                          <FiKey />
                          {apiKeyLoading ? 'Generating...' : 'Generate API Key'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="settings-group">
                <h3><FiShield /> Session Security</h3>
                <div className="setting-item">
                  <div className="setting-info">
                    <span className="setting-label">Two-Factor Authentication</span>
                    <span className="setting-description">Add an extra layer of security</span>
                  </div>
                  <ToggleSwitch 
                    enabled={settings.twoFactorAuth}
                    onToggle={() => toggleSetting('twoFactorAuth')}
                  />
                </div>
                
                <div className="setting-item">
                  <div className="setting-info">
                    <span className="setting-label">Auto Logout</span>
                    <span className="setting-description">Automatically logout after inactivity</span>
                  </div>
                  <ToggleSwitch 
                    enabled={settings.autoLogout}
                    onToggle={() => toggleSetting('autoLogout')}
                  />
                </div>
                
                {settings.autoLogout && (
                  <div className="setting-item">
                    <div className="setting-info">
                      <span className="setting-label">Auto Logout Time</span>
                      <span className="setting-description">Minutes before automatic logout</span>
                    </div>
                    <input
                      type="number"
                      className="form-control number-control"
                      value={settings.autoLogoutTime}
                      onChange={(e) => setSettings({ ...settings, autoLogoutTime: parseInt(e.target.value) })}
                      min="5"
                      max="120"
                    />
                  </div>
                )}
                
                <div className="setting-item">
                  <div className="setting-info">
                    <span className="setting-label">Remember This Device</span>
                    <span className="setting-description">Stay logged in on this device</span>
                  </div>
                  <ToggleSwitch 
                    enabled={settings.rememberDevice}
                    onToggle={() => toggleSetting('rememberDevice')}
                  />
                </div>
              </div>

              {settingsChanged && (
                <div className="form-actions">
                  <button
                    onClick={saveSettings}
                    className="btn btn-primary"
                    disabled={isSavingSettings}
                  >
                    <FiSave />
                    {isSavingSettings ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* API Documentation Tab */}
          {activeTab === 'api-docs' && (
            <div className="settings-section api-docs-tab">
              <ApiDocs />
            </div>
          )}
        </div>
      </div>

      {/* API Key Display Modal - Only shown when generating new key */}
      {showApiKeyModal && displayedApiKey && (
        <div className="modal-overlay" onClick={closeApiKeyModal}>
          <div className="modal-content api-key-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><FiKey /> API Key Generated</h2>
              <button className="btn-close" onClick={closeApiKeyModal}>
                <FiX />
              </button>
            </div>

            <div className="api-key-modal-content">
              <div className="api-key-warning">
                <strong>⚠️ Important:</strong> Please copy your API key now and store it securely.
              </div>

              <div className="api-key-display">
                <code className="api-key-code">{displayedApiKey}</code>
                <button 
                  className={`btn-copy ${apiKeyCopied ? 'copied' : ''}`}
                  onClick={copyModalApiKeyToClipboard}
                  title={apiKeyCopied ? 'Copied!' : 'Copy to clipboard'}
                >
                  {apiKeyCopied ? (
                    <>
                      <FiCheck />
                      <span>Copied!</span>
                    </>
                  ) : (
                    <>
                      <FiCopy />
                      <span>Copy</span>
                    </>
                  )}
                </button>
              </div>

              <div className="api-key-details">
                <p><strong>User:</strong> {user?.username}</p>
                <p><strong>Created:</strong> {apiKeyCreatedAt ? new Date(apiKeyCreatedAt).toLocaleString() : new Date().toLocaleString()}</p>
              </div>

              <div className="form-actions">
                <button className="btn btn-primary" onClick={closeApiKeyModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
